// src/component-files/reports/OperatingStatementReport.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Typography,
    Divider,
    Select,
    MenuItem,
    Button,
    CircularProgress,
} from "@mui/material";

import "./OperatingStatementReport.scss";
import TableWithPagination from "../../components/TableWithPagination/TableWithPagination";
import figLogo from "../../assets/fig_CROP.png";
import pdf2icons from "../../assets/pdf2-icons.png";
import excelIcon from "../../assets/excel-icons.png";

import {
    formatCurrency,
    calculateCategoryTotals,
    fetchExpenses,
    fetchIncomes,
    fetchMealCounts,
    exportOperatingStatementExcel,
} from "../../utils/utils";
import { useReactToPrint } from "react-to-print";
import {
    expenseHeaders,
    incomeHeaders,
    mealHeaders,
} from "./reportTableHeaders";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";

const API_ENDPOINTS = {
    expenses: "https://figazuresql.swancreate.com/api/swa/FINANCE_expenses",
    incomes: "https://figazuresql.swancreate.com/api/swa/FINANCE_incomes",
    mealCounts: "https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts",
};

/* --------------------------- School Year helpers --------------------------- */
// School year runs Jul 1 (Y) → Jun 30 (Y+1)
const getSYStartFromDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt)) return null;
    const m = dt.getMonth(); // 0..11
    return m >= 6 ? dt.getFullYear() : dt.getFullYear() - 1;
};
const formatSYLabel = (startYear) => `${startYear}–${startYear + 1}`;

// Try to pull a Date from an arbitrary record (best-effort for meal counts)
const getAnyDate = (row, preferredKeys = []) => {
    // preferred: use known keys first
    for (const k of preferredKeys) {
        const v = row?.[k];
        if (v && !isNaN(new Date(v))) return new Date(v);
    }
    // fallback: scan keys with "date" in name
    for (const key of Object.keys(row || {})) {
        if (/date/i.test(key)) {
            const v = row[key];
            const dt = new Date(v);
            if (!isNaN(dt)) return dt;
        }
    }
    return null;
};

const selectCss = {
    color: "#000",
    border: 0,
    minWidth: 200,
    height: "50px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #F0F0F0 !important",
        borderRadius: "10px",
    },
};

const StatBlock = ({ label, value }) => (
    <Box className="stat-box">
        <h3 className="stat-title">{label}</h3>
        <p className="stat-value">{formatCurrency(value)}</p>
    </Box>
);

const TableSection = ({
    title,
    headers,
    data,
    pdfPrint,
    selectCss,
    selectedCategory,
    setSelectedCategory,
    totals,
    totalProfit,
}) => (
    <>
        <Box
            className="table-section"
            style={{
                border: "1px solid #F0F0F0",
                borderRadius: "20px",
                overflow: "hidden",
            }}
        >
            <div
                className="tablehead"
                style={{
                    padding: "0 21px",
                    backgroundColor: "#F0F0F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 20px",
                }}
            >
                <Typography fontSize={18} fontWeight={500}>
                    {title}
                </Typography>
                {!pdfPrint && !title.toLowerCase().includes("meal count") && (
                    <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        sx={{ ...selectCss, minWidth: 250 }}
                    >
                        <MenuItem value="all">
                            <b>All :</b> {formatCurrency(totalProfit)}
                        </MenuItem>
                        {Object.entries(totals).map(([cat, { total }]) => (
                            <MenuItem key={cat} value={cat}>
                                <b>{cat} :</b> {formatCurrency(total)}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </div>
            <TableWithPagination
                headers={headers}
                data={data}
                selectable
                defaultLimit={10}
                pdfPrint={pdfPrint}
            />
        </Box>
    </>
);

export default function OperatingStatementReport() {
    const [selectedMonth, setSelectedMonth] = useState("All");
    const [incomeData, setIncomeData] = useState([]);
    const [expenseData, setExpenseData] = useState([]);
    const [mealCountData, setMealCountData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfPrint, setPdfPrint] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [summaryPdf, setSummaryPdf] = useState(false);
    const [selectedIncomeCategory, setSelectedIncomeCategory] = useState("all");
    const [selectedExpenseCategory, setSelectedExpenseCategory] = useState("all");

    // NEW: School Year state (default to current)
    const [selectedSYStart, setSelectedSYStart] = useState(
        getSYStartFromDate(new Date())
    );

    const reportRef = useRef(null);
    const user = localStorage.getItem("user");
    const userDetails = user ? JSON.parse(user) : null;

    const handleAfterPrint = React.useCallback(() => {
        setPdfPrint(false);
        setSummaryPdf(false);
    }, []);

    const handlePrint = useReactToPrint({
        contentRef: reportRef,
        documentTitle: `P&L_${userDetails?.department || "General"}_${new Date().getFullYear()}-${selectedMonth === "All"
                ? "ALL"
                : new Date(0, selectedMonth - 1).toLocaleString("default", {
                    month: "short",
                })
            }`,
        removeAfterPrint: true,
        onAfterPrint: handleAfterPrint,
    });

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchExpenses(API_ENDPOINTS.expenses, selectedMonth),
            fetchIncomes(API_ENDPOINTS.incomes, selectedMonth),
            fetchMealCounts(API_ENDPOINTS.mealCounts, selectedMonth),
        ])
            .then(([expenses, incomes, meals]) => {
                setExpenseData(expenses);
                setIncomeData(incomes);
                setMealCountData(meals);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [selectedMonth]);

    /* --------------------------- Build SY options --------------------------- */
    const availableSYStarts = useMemo(() => {
        const starts = new Set();

        // From incomes
        incomeData.forEach((r) => {
            const d =
                r.IncomeDate || r.Date || r.date || r.Income_Dt || r["Income.Date"];
            const sy = d ? getSYStartFromDate(d) : null;
            if (sy) starts.add(sy);
        });

        // From expenses
        expenseData.forEach((r) => {
            const d =
                r.ExpenseDate || r.Date || r.date || r.Expense_Dt || r["Expense.Date"];
            const sy = d ? getSYStartFromDate(d) : null;
            if (sy) starts.add(sy);
        });

        // If nothing detected yet, include current ±1 for safety
        if (starts.size === 0) {
            const cur = getSYStartFromDate(new Date());
            return [cur + 1, cur, cur - 1];
        }

        return Array.from(starts).sort((a, b) => b - a);
    }, [incomeData, expenseData]);

    /* -------------------------- Filter by School Year ----------------------- */
    const incomeDataSY = useMemo(() => {
        return incomeData.filter((r) => {
            const d =
                r.IncomeDate || r.Date || r.date || r.Income_Dt || r["Income.Date"];
            const sy = d ? getSYStartFromDate(d) : null;
            return sy === selectedSYStart;
        });
    }, [incomeData, selectedSYStart]);

    const expenseDataSY = useMemo(() => {
        return expenseData.filter((r) => {
            const d =
                r.ExpenseDate || r.Date || r.date || r.Expense_Dt || r["Expense.Date"];
            const sy = d ? getSYStartFromDate(d) : null;
            return sy === selectedSYStart;
        });
    }, [expenseData, selectedSYStart]);

    const mealCountDataSY = useMemo(() => {
        // Best-effort: look for a usable date on each row
        return mealCountData.filter((row) => {
            const dt =
                getAnyDate(row, [
                    "MealDate",
                    "Date",
                    "ReportDate",
                    "CreatedDate",
                    "IncomeDate",
                    "ExpenseDate",
                ]) || null;
            if (!dt) return true; // if no date field, don't exclude
            return getSYStartFromDate(dt) === selectedSYStart;
        });
    }, [mealCountData, selectedSYStart]);

    /* ------------------- Category filters (within SY) ----------------------- */
    const filteredIncomeData = useMemo(() => {
        if (isExporting || selectedIncomeCategory === "all") return incomeDataSY;
        return incomeDataSY.filter(
            (item) => item.IncomeCategory === selectedIncomeCategory
        );
    }, [incomeDataSY, selectedIncomeCategory, isExporting]);

    const filteredExpenseData = useMemo(() => {
        if (isExporting || selectedExpenseCategory === "all") return expenseDataSY;
        return expenseDataSY.filter(
            (item) => item.ExpenseCategory === selectedExpenseCategory
        );
    }, [expenseDataSY, selectedExpenseCategory, isExporting]);

    /* --------------------------- Totals (by SY) ----------------------------- */
    const expenseTotals = useMemo(
        () => calculateCategoryTotals(expenseDataSY, "ExpenseCategory"),
        [expenseDataSY]
    );
    const incomeTotals = useMemo(
        () => calculateCategoryTotals(incomeDataSY, "IncomeCategory"),
        [incomeDataSY]
    );

    const totalIncome = useMemo(
        () => incomeDataSY.reduce((sum, i) => sum + (i.TotalAmount || 0), 0),
        [incomeDataSY]
    );
    const totalExpenses = useMemo(
        () => expenseDataSY.reduce((sum, e) => sum + (e.TotalAmount || 0), 0),
        [expenseDataSY]
    );
    const net = totalIncome - totalExpenses;

    /* ------------------------ Meal counts (by SY) --------------------------- */
    const groupedMealCounts = useMemo(() => {
        const groups = {};
        mealCountDataSY.forEach((row) => {
            const site = row["Income.SchoolName"] || row.SchoolName || "Unknown";
            groups[site] = groups[site] || { ...row, Summary_Total: 0 };
            Object.keys(row).forEach((key) => {
                if (key.startsWith("Income.") || key === "Summary_Total") {
                    groups[site][key] =
                        (groups[site][key] || 0) + (Number(row[key]) || 0);
                }
            });
        });
        return Object.values(groups);
    }, [mealCountDataSY]);

    if (loading)
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="-webkit-fill-available"
            >
                <CircularProgress />
            </Box>
        );

    const handleFullPdfPrint = () => {
        setIsExporting(true);
        setPdfPrint(true);
        setTimeout(() => {
            handlePrint();
            setIsExporting(false);
        }, 50);
    };

    const summaryPrint = () => {
        setIsExporting(true);
        setSummaryPdf(true);
        setTimeout(() => {
            handlePrint();
            setIsExporting(false);
        }, 50);
    };

    const handleExcelExport = () => {
        setIsExporting(true);
        setTimeout(() => {
            exportOperatingStatementExcel({
                userDetails,
                totalIncome,
                totalExpenses,
                net,
                incomeData: incomeDataSY, // export within SY
                expenseData: expenseDataSY, // export within SY
                groupedMealCounts,
                getDepartment: () => userDetails?.department || "General",
                getYear: () => new Date().getFullYear(),
                getMonthString: () =>
                    selectedMonth === "All"
                        ? "ALL"
                        : `${new Date(0, selectedMonth - 1).toLocaleString("default", {
                            month: "short",
                        })}`,
                formatCurrency,
            });
            setIsExporting(false);
        }, 50);
    };

    return (
        <>
            <div className="pagedjs_report print-container" ref={reportRef}>
                <div className="header">
                    <div className={`header-top ${pdfPrint ? "centered" : ""}`}>
                        <h1>Operating Statement Report</h1>

                        {!pdfPrint && (
                            <div className="no-print report-controls" style={{ gap: 12, display: "flex", flexWrap: "wrap" }}>
                                {/* NEW: School Year select (fits with existing controls) */}
                                <Select
                                    value={selectedSYStart}
                                    onChange={(e) => setSelectedSYStart(Number(e.target.value))}
                                    sx={{ ...selectCss, minWidth: 220 }}
                                >
                                    {availableSYStarts.map((sy) => (
                                        <MenuItem key={sy} value={sy}>
                                            {formatSYLabel(sy)}
                                        </MenuItem>
                                    ))}
                                </Select>

                                {/* Existing Month select */}
                                <Select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    sx={selectCss}
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <MenuItem key={i} value={i + 1}>
                                            {new Date(0, i).toLocaleString("default", {
                                                month: "long",
                                            })}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <Button className="reportBtn" onClick={() => handleFullPdfPrint()}>
                                    Download as PDF <img src={pdf2icons} alt="pdf" width={27} />
                                </Button>
                                <Button className="reportBtn" onClick={handleExcelExport}>
                                    Download as Excel <img src={excelIcon} alt="" />
                                </Button>
                                <Button className="reportBtn" onClick={() => summaryPrint()}>
                                    Download Summary
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="header-details">
                        <div className="logo">
                            <img src={figLogo} alt="Logo" />
                        </div>
                        <div className="report-summary">
                            <h2>{userDetails?.schoolDistrictName || "N/A"}</h2>
                            <p>
                                Total Income: <span>{formatCurrency(totalIncome)}</span>
                            </p>
                            <p>
                                Total Expenses: <span>{formatCurrency(totalExpenses)}</span>
                            </p>
                            <p>
                                {net >= 0 ? "Net Profit: " : "Net Loss: "}
                                <span>{formatCurrency(net)}</span>
                            </p>
                            <p style={{ marginTop: 6, opacity: 0.85 }}>
                                School Year: <b>{formatSYLabel(selectedSYStart)}</b>
                            </p>
                        </div>
                    </div>

                    <div className="stat-boxes">
                        <div className="box">
                            <div className="stat-container">
                                <StatBlock label="Total Income" value={totalIncome} />
                                <StatBlock label="Total Expenses" value={totalExpenses} />
                                <StatBlock label="Net Profit/Loss" value={net} />
                            </div>
                        </div>

                        <div className="box">
                            <Typography variant="h5" className="box-title">
                                Income by Category
                            </Typography>
                            <div className="stat-container">
                                {Object.entries(incomeTotals).map(([cat, { total }]) => (
                                    <StatBlock key={cat} label={cat} value={total} />
                                ))}
                            </div>
                        </div>

                        <div className="box">
                            <Typography variant="h5" className="box-title">
                                Expenses by Category
                            </Typography>
                            <div className="stat-container">
                                {Object.entries(expenseTotals).map(([cat, { total }]) => (
                                    <StatBlock key={cat} label={cat} value={total} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {!summaryPdf && (
                    <>
                        <div className="tables">
                            <TableSection
                                title={
                                    selectedIncomeCategory
                                        ? `Income - ${selectedIncomeCategory}`
                                        : "Income"
                                }
                                headers={incomeHeaders}
                                data={filteredIncomeData}
                                pdfPrint={pdfPrint}
                                selectedCategory={selectedIncomeCategory}
                                setSelectedCategory={setSelectedIncomeCategory}
                                totals={incomeTotals}
                                selectCss={selectCss}
                                totalProfit={totalIncome}
                            />

                            <TableSection
                                title={
                                    selectedExpenseCategory
                                        ? `Expenses - ${selectedExpenseCategory}`
                                        : "Expenses"
                                }
                                headers={expenseHeaders}
                                data={filteredExpenseData}
                                pdfPrint={pdfPrint}
                                selectedCategory={selectedExpenseCategory}
                                setSelectedCategory={setSelectedExpenseCategory}
                                totals={expenseTotals}
                                selectCss={selectCss}
                                totalProfit={totalExpenses}
                            />

                            <TableSection
                                title="Meal Counts"
                                headers={mealHeaders}
                                data={groupedMealCounts}
                                pdfPrint={pdfPrint}
                            />
                        </div>

                        <div className="summary-section">
                            <Typography variant="h5">Overall Summary</Typography>
                            <Divider sx={{ my: 1 }} />
                            <Box className="summary-total">
                                <Typography>
                                    Total Income: {formatCurrency(totalIncome)}
                                </Typography>
                                <Typography>
                                    Total Expenses: {formatCurrency(totalExpenses)}
                                </Typography>
                            </Box>
                            <Typography className="summary-net" variant="h6">
                                {net >= 0 ? "Net Profit: " : "Net Loss: "}
                                {formatCurrency(net)}
                            </Typography>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
